import React from 'react';
import { PosterSettings } from '@/types/canvas';
import { cn } from '@/lib/utils';
import { getAllVariants, type ProductVariant } from '@/lib/variants';

interface SizeSelectorProps {
    posterSettings: PosterSettings;
    onPosterSettingsChange: (settings: Partial<PosterSettings>) => void;
}

export function SizeSelector({ posterSettings, onPosterSettingsChange }: SizeSelectorProps) {
    const [variants, setVariants] = React.useState<ProductVariant[]>([]);
    // const [loading, setLoading] = React.useState(true);

    React.useEffect(() => {
        async function loadVariants() {
            try {
                const posterVariants = await getAllVariants({
                    frameType: 'none',
                    onlyAvailable: false,
                });

                // Ensure we have unique sizes
                const uniqueSizes = new Map();
                posterVariants.forEach(v => {
                    if (!uniqueSizes.has(v.size)) {
                        uniqueSizes.set(v.size, v);
                    }
                });

                setVariants(Array.from(uniqueSizes.values()));
            } catch (error) {
                console.error('Error loading poster variants:', error);
            } finally {
                // setLoading(false);
            }
        }
        loadVariants();
    }, []);

    const getBasePrice = (discountedPrice: number): number => {
        return discountedPrice / 0.7;
    };

    const getSizeLabel = (size: string): string => {
        const sizeMap: Record<string, string> = {
            'B2': 'B2 (50x70)',
            '50x70': 'B2 (50x70)',
            'A2': 'A2 (42x59)',
            'A3': 'A3 (30x42)',
            'A4': 'A4 (21x30)',
        };
        return sizeMap[size] || size;
    };

    const getSizeImage = (size: string): string => {
        if (size === 'B2' || size === '50x70') return '/sizes/B2.jpg';
        if (size === 'A2') return '/sizes/A2.jpg';
        return '';
    };

    const displayVariants = variants.length > 0 ? variants : [
        { id: 'b2_fallback', size: 'B2', price_cents: 4900, currency: 'EUR' } as ProductVariant,
        { id: 'a2_fallback', size: 'A2', price_cents: 3900, currency: 'EUR' } as ProductVariant
    ];

    return (
        <div className="space-y-4">
            <div className="flex gap-4 overflow-x-auto pb-4 -mx-4 px-4 snap-x [scrollbar-width:auto] [&::-webkit-scrollbar]:block">
                {displayVariants.map((variant) => {
                    const isSelected = posterSettings.size === variant.size;
                    const sizeLabel = getSizeLabel(variant.size);
                    const bgImage = getSizeImage(variant.size);
                    const discountedPrice = variant.price_cents ? variant.price_cents / 100 : 0;
                    const finalPrice = discountedPrice || (variant.size === 'B2' ? 49 : 39);
                    const basePrice = getBasePrice(finalPrice);

                    return (
                        <button
                            key={variant.id || variant.size}
                            onClick={() => onPosterSettingsChange({ size: variant.size as any })}
                            className={cn(
                                'flex-none w-[140px] flex flex-col items-center gap-3 rounded-xl border-2 p-3 transition snap-start text-center bg-white',
                                isSelected
                                    ? 'border-green-dark bg-green-dark/5 shadow-sm'
                                    : 'border-transparent hover:border-green-dark/30 shadow-sm'
                            )}
                        >
                            <div className="relative w-20 h-24 bg-gray-100 border border-gray-200 shadow-sm overflow-hidden rounded-md flex-shrink-0">
                                {bgImage ? (
                                    <img
                                        src={bgImage}
                                        alt={sizeLabel}
                                        className="w-full h-full object-contain"
                                    />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-xs text-muted-foreground">
                                        {variant.size}
                                    </div>
                                )}
                            </div>

                            <div className="flex flex-col items-center min-w-0 w-full">
                                <span className="text-sm font-semibold text-foreground truncate w-full">{sizeLabel.split(' ')[0]}</span>
                                <span className="text-[10px] text-muted-foreground truncate w-full mb-1">{sizeLabel.split(' ').slice(1).join(' ').replace('(', '').replace(')', '')}</span>

                                <div className="flex flex-col items-center">
                                    <span className="font-bold text-foreground text-sm">{finalPrice.toFixed(2)}€</span>
                                    <span className="text-[10px] text-muted-foreground line-through opacity-70">{basePrice.toFixed(2)}€</span>
                                </div>
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
